import "./dutyRequest.page.css";
import React, { useEffect, useState } from "react";
import { Navbar } from "@src/components/navbar";
import { useMutation, useQuery } from "@apollo/client";
import { getAllSitesForCustomerQuery } from "@src/util/query/site.query";
import { cancelDutyRequestMutation } from "@src/util/mutation/dutyRequest.mutation";
import { getCustomerDutyRequestsQuery } from "@src/util/query/dutyRequest.query";
import { Table, Tag, message, Popconfirm, Spin } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { DutyRequestFormModal } from "@src/components/dutyRequestForm/dutyRequestFormModal";

/* ─── Main Page ─── */
export const DutyRequest = () => {
    const [customerDetail, setCustomerDetail] = useState(null);
    const [newRequestModal, setNewRequestModal] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");
    const [cancellingId, setCancellingId] = useState(null);
    const [cancelRequest] = useMutation(cancelDutyRequestMutation);

    const handleCancel = async (requestId) => {
        setCancellingId(requestId);
        try {
            await cancelRequest({ variables: { requestId } });
            message.success("Request cancelled successfully");
            refetch();
        } catch (err) {
            message.error(err?.message || "Failed to cancel request");
        } finally {
            setCancellingId(null);
        }
    };

    useEffect(() => {
        const detail = localStorage.getItem("customerDetail");
        if (detail) setCustomerDetail(JSON.parse(detail));
    }, []);

    const { data: sitesData, loading: sitesLoading } = useQuery(getAllSitesForCustomerQuery, {
        variables: { customerId: customerDetail?._id },
        skip: !customerDetail?._id,
        fetchPolicy: "network-only",
    });

    const { data: requestsData, loading: requestsLoading, refetch } = useQuery(
        getCustomerDutyRequestsQuery,
        { fetchPolicy: "network-only" }
    );

    const allSites = sitesData?.getAllSiteForCustomer?.response || [];
    const allRequests = requestsData?.getCustomerDutyRequests?.response || [];

    const filteredRequests = statusFilter === "all"
        ? allRequests
        : allRequests.filter(r => r.status === statusFilter);

    const statusColors = {
        pending:  { bg: "#fff3cd", color: "#856404", border: "#ffd97d" },
        pushed:   { bg: "#d1e7dd", color: "#0a5940", border: "#a3cfbb" },
        rejected: { bg: "#f8d7da", color: "#842029", border: "#f1aeb5" },
    };

    const priorityDot = { high: "#FF4D4F", medium: "#FA8C16", low: "#52C41A" };

    const tabCounts = {
        all: allRequests.length,
        pending: allRequests.filter(r => r.status === "pending").length,
        pushed: allRequests.filter(r => r.status === "pushed").length,
        rejected: allRequests.filter(r => r.status === "rejected").length,
    };

    const columns = [
        {
            title: "Site",
            dataIndex: "siteDetail",
            render: (_, r) => (
                <div>
                    <div style={{ fontWeight: 500, color: "#33475b" }}>{r.siteDetail?.sitename || r.siteId}</div>
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>{r.siteDetail?.postcode}</div>
                </div>
            ),
        },
        {
            title: "Service Type",
            dataIndex: "serviceType",
            render: v => (
                <Tag style={{
                    background: v === "guarding" ? "#e0e7ff" : "#fae8ff",
                    color: v === "guarding" ? "#3730a3" : "#7e22ce",
                    border: "none", borderRadius: 4, fontWeight: 500, textTransform: "capitalize",
                }}>
                    {v === "guarding" ? "Guarding" : "Mobile Response"}
                </Tag>
            ),
        },
        {
            title: "Schedule",
            width: 120,
            render: (_, r) => {
                if (r.serviceType !== "mobile" || !r.scheduleType) return <span style={{ color: "#9ca3af" }}>—</span>;
                if (r.scheduleType === "one-off") return <span style={{ fontSize: 13 }}>One-off</span>;
                const sub = r.scheduleDays?.length > 0
                    ? r.scheduleDays.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(", ")
                    : r.frequencyDays ? `Every ${r.frequencyDays} days` : null;
                return (
                    <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>UFN</div>
                        {sub && <div style={{ fontSize: 11, color: "#9ca3af" }}>{sub}</div>}
                    </div>
                );
            },
        },
        {
            title: "No. of Duties",
            dataIndex: "numberOfDuties",
            width: 110,
            render: (v, r) => r.serviceType === "guarding"
                ? <span style={{ fontWeight: 600, color: "#33475b" }}>{v ?? "—"}</span>
                : <span style={{ color: "#9ca3af" }}>—</span>,
        },
        {
            title: "Preferred Date / Time",
            render: (_, r) => {
                const date = r.preferredDate || (r.preferredStartTime ? r.preferredStartTime.split(" ")[0] : null);
                const time = r.preferredStartTime && r.preferredEndTime
                    ? `${r.preferredStartTime} – ${r.preferredEndTime}`
                    : r.preferredStartTime || null;
                return (
                    <div>
                        <div style={{ fontWeight: 500 }}>{date || "—"}</div>
                        <div style={{ fontSize: 12, color: "#9ca3af" }}>{time || ""}</div>
                    </div>
                );
            },
        },
        {
            title: "Priority",
            dataIndex: "priority",
            render: v => v ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: priorityDot[v] || "#ccc", display: "inline-block" }} />
                    <span style={{ textTransform: "capitalize" }}>{v}</span>
                </div>
            ) : "—",
        },
        {
            title: "Status",
            dataIndex: "status",
            render: v => {
                const s = statusColors[v] || {};
                return (
                    <span style={{
                        display: "inline-block", padding: "3px 10px", borderRadius: 4,
                        fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px",
                        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                    }}>
                        {v}
                    </span>
                );
            },
        },
        {
            title: "Submitted",
            dataIndex: "createdAt",
            render: v => v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—",
        },
        {
            title: "",
            width: 120,
            render: (_, r) => (
                <div style={{ display: "flex", gap: 6 }}>
                    {(r.status === "pending" || r.status === "rejected") && (
                        <Popconfirm
                            title="Delete this request?"
                            description="This action cannot be undone."
                            okText="Yes, delete"
                            cancelText="No"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleCancel(r._id)}
                        >
                            <button
                                style={{
                                    background: "none", border: "1px solid #f1aeb5",
                                    color: "#dc2626", borderRadius: 6, padding: "4px 10px",
                                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                                    opacity: cancellingId === r._id ? 0.5 : 1,
                                }}
                                disabled={cancellingId === r._id}
                            >
                                {cancellingId === r._id ? <Spin indicator={<LoadingOutlined style={{ fontSize: 11 }} spin />} /> : (r.status === "pending" ? "Cancel" : "Delete")}
                            </button>
                        </Popconfirm>
                    )}
                </div>
            ),
        },
    ];

    const tabs = [
        { key: "all", label: "All" },
        { key: "pending", label: "Pending" },
        { key: "pushed", label: "Pushed" },
        { key: "rejected", label: "Rejected" },
    ];

    return (
        <div>
            <Navbar customerDetail={customerDetail} />

            <div style={{ padding: 'var(--page-pad)' }}>

                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 }}>
                    <div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#33475b' }}>Duty Requests</div>
                        <div style={{ fontSize: 13, color: '#7c98b6', marginTop: 4 }}>Submit and track your duty requests</div>
                    </div>
                    <button
                        className="drawer-filled-btn"
                        style={{ display: "flex", alignItems: "center", gap: 8 }}
                        onClick={() => setNewRequestModal(true)}
                        disabled={sitesLoading || allSites.length === 0}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        New Request
                    </button>
                </div>

                {/* Status Tabs */}
                <div style={{
                    display: 'flex', gap: 4, marginBottom: 20,
                    borderBottom: '1px solid #dfe3eb', paddingBottom: 0,
                }}>
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setStatusFilter(tab.key)}
                            style={{
                                padding: '8px 16px',
                                fontSize: 13,
                                fontWeight: 600,
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                color: statusFilter === tab.key ? '#126e7d' : '#7c98b6',
                                borderBottom: statusFilter === tab.key ? '2px solid #126e7d' : '2px solid transparent',
                                marginBottom: -1,
                                transition: 'all 0.15s',
                            }}
                        >
                            {tab.label}
                            <span style={{
                                marginLeft: 6, fontSize: 11, fontWeight: 500,
                                background: statusFilter === tab.key ? '#e5f5f8' : '#f5f8fa',
                                color: statusFilter === tab.key ? '#126e7d' : '#7c98b6',
                                padding: '1px 6px', borderRadius: 10,
                            }}>
                                {tabCounts[tab.key]}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Table */}
                <Table
                    className="requests-table"
                    columns={columns}
                    dataSource={filteredRequests.map(r => ({ ...r, key: r._id }))}
                    loading={requestsLoading}
                    locale={{ emptyText: "No duty requests found" }}
                    pagination={{ pageSize: 15, showSizeChanger: false }}
                    style={{ background: '#fff', border: '1px solid #dfe3eb', borderRadius: 6 }}
                />
            </div>

            {newRequestModal && (
                <DutyRequestFormModal
                    visible={newRequestModal}
                    onClose={() => setNewRequestModal(false)}
                    sites={allSites}
                    onSuccess={() => refetch()}
                />
            )}
        </div>
    );
};
